import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { AtriumEvent } from '@cdvi-edge/shared';

const apiBase = import.meta.env.VITE_WORKER_URL ?? '';

interface Ctx {
  events: AtriumEvent[];
  connected: boolean;
}

const LiveCtx = createContext<Ctx>({ events: [], connected: false });

export function LiveStreamProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const [events, setEvents] = useState<AtriumEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const reconnectTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }
    let es: EventSource | null = null;

    function open() {
      const url = `${apiBase}/events/stream`;
      es = new EventSource(url, { withCredentials: true });
      es.onopen = () => setConnected(true);
      es.onerror = () => {
        setConnected(false);
        es?.close();
        // Native EventSource auto-reconnects, but only on transport errors.
        // We re-create on close to handle 401 / 5xx too.
        reconnectTimer.current = window.setTimeout(open, 3000);
      };
      es.addEventListener('atrium', (msg) => {
        try {
          const evt = JSON.parse((msg as MessageEvent).data) as AtriumEvent;
          setEvents((prev) => [evt, ...prev].slice(0, 500));
        } catch {
          /* ignore malformed */
        }
      });
    }
    open();

    return () => {
      es?.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [enabled]);

  return <LiveCtx.Provider value={{ events, connected }}>{children}</LiveCtx.Provider>;
}

export function useLiveStream() {
  return useContext(LiveCtx);
}

export function useLiveStreamConnection() {
  return { connected: useContext(LiveCtx).connected };
}
